import { useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  KeyboardDoubleArrowLeft as FirstPageIcon,
  KeyboardDoubleArrowRight as LastPageIcon,
} from '@mui/icons-material'

export interface Column {
  id: string
  label: string
  align?: 'left' | 'right' | 'center'
  width?: string | number
  minWidth?: string | number
  sortable?: boolean
  render?: (value: any, row: any, index: number) => ReactNode
}

interface DataTablePagination {
  page: number
  pageSize: number
  total: number
  totalPages: number
  itemLabel?: string
  onPageChange: (page: number) => void
  pageSizeOptions?: number[]
  onPageSizeChange?: (pageSize: number) => void
}

interface DataTableProps {
  columns: Column[]
  rows: any[]
  onRowClick?: (row: any) => void
  emptyMessage?: string
  emptyHint?: string
  emptyIcon?: ReactNode
  selectable?: boolean
  showRowNumbers?: boolean
  isLoading?: boolean
  isFetching?: boolean
  rowKey?: (row: any, index: number) => string
  selectedIds?: Set<string>
  onSelectionChange?: (selectedIds: Set<string>) => void
  getRowSx?: (row: any, index: number, selected: boolean) => SxProps<Theme>
  pagination?: DataTablePagination
  defaultPageSize?: number
}

const TABLE_BORDER = '#E4E0EC'
const HEADER_BG = '#F7F5FC'
const ROW_BORDER = '#E9E7EF'
const BODY_TEXT = '#2F2F3A'
const MUTED_TEXT = '#6D6878'
const CHECK_COLOR = '#B8B3C2'
const ACTIVE_PURPLE = '#5C35D8'
const PAGINATION_ACTIVE_BG = '#E3D5FF'
const PAGINATION_ACTIVE_FG = '#4B3082'

function getVisiblePages(page: number, totalPages: number) {
  if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1)

  const pages = new Set([1, totalPages, page])
  if (page > 1) pages.add(page - 1)
  if (page < totalPages) pages.add(page + 1)

  return [...pages].sort((a, b) => a - b).reduce<(number | 'ellipsis')[]>((acc, value, index, arr) => {
    if (index > 0 && value - arr[index - 1] > 1) acc.push('ellipsis')
    acc.push(value)
    return acc
  }, [])
}

export const DataTable = ({
  columns,
  rows,
  onRowClick,
  emptyMessage = 'No records found',
  emptyHint,
  emptyIcon,
  selectable = true,
  showRowNumbers = false,
  isLoading = false,
  isFetching = false,
  rowKey,
  selectedIds,
  onSelectionChange,
  getRowSx,
  pagination,
  defaultPageSize = 10,
}: DataTableProps) => {
  const [internalSelected, setInternalSelected] = useState<Set<string>>(new Set())
  const [sortBy, setSortBy] = useState<string>('')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [internalPage, setInternalPage] = useState(1)
  const [internalPageSize, setInternalPageSize] = useState(defaultPageSize)

  const getRowId = (row: any, index: number) => rowKey?.(row, index) ?? row.id ?? String(index)
  const rowIds = useMemo(() => rows.map(getRowId), [rows, rowKey])
  const selected = selectedIds ?? internalSelected

  // Internal client-side pagination (used when no external pagination prop is provided)
  const useInternalPaging = !pagination
  const totalInternalPages = useInternalPaging ? Math.max(1, Math.ceil(rows.length / internalPageSize)) : 1
  const pagedRows = useInternalPaging
    ? rows.slice((internalPage - 1) * internalPageSize, internalPage * internalPageSize)
    : rows

  const internalPagination: DataTablePagination | undefined = useInternalPaging && rows.length > 0 ? {
    page:       internalPage,
    pageSize:   internalPageSize,
    total:      rows.length,
    totalPages: totalInternalPages,
    onPageChange:     (p) => setInternalPage(p),
    onPageSizeChange: (s) => { setInternalPageSize(s); setInternalPage(1) },
    pageSizeOptions:  [10, 20, 50, 100],
  } : undefined

  const activePagination = pagination ?? internalPagination
  const displayRows = useInternalPaging ? pagedRows : rows

  useEffect(() => {
    if (!selectedIds) setInternalSelected(prev => new Set([...prev].filter(id => rowIds.includes(id))))
  }, [rowIds, selectedIds])

  useEffect(() => {
    if (useInternalPaging) setInternalPage(1)
  }, [rows.length])

  const updateSelected = (next: Set<string>) => {
    if (!selectedIds) setInternalSelected(next)
    onSelectionChange?.(next)
  }

  const handleSelectAll = (checked: boolean) => {
    const pageRowIds = displayRows.map((r, i) => getRowId(r, i))
    updateSelected(checked ? new Set(pageRowIds) : new Set())
  }

  const handleSelect = (id: string) => {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id); else next.add(id)
    updateSelected(next)
  }

  const handleSort = (colId: string) => {
    if (sortBy === colId) setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    else { setSortBy(colId); setSortDir('asc') }
  }

  const colSpan = columns.length + (selectable ? 1 : 0) + (showRowNumbers ? 1 : 0)
  const allChecked = displayRows.length > 0 && selected.size === displayRows.length
  const someChecked = selected.size > 0 && selected.size < displayRows.length

  return (
    <Box
      sx={{
        backgroundColor: '#FFFFFF',
        border: `1px solid ${TABLE_BORDER}`,
        borderRadius: '4px',
        boxShadow: 'none',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <TableContainer sx={{ flexGrow: 1, overflow: 'auto', position: 'relative' }}>
        {isFetching && !isLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 36,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.65)',
              backdropFilter: 'blur(1px)',
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={28} sx={{ color: ACTIVE_PURPLE }} />
          </Box>
        )}
        <Table stickyHeader size="small" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow sx={{ height: 36 }}>
            {selectable && (
              <TableCell padding="none" sx={{ width: 40, pl: '10px', py: 0, borderBottom: `1px solid ${TABLE_BORDER}`, backgroundColor: HEADER_BG, verticalAlign: 'middle' }}>
                <Checkbox
                  size="small"
                  indeterminate={someChecked}
                  checked={allChecked}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  sx={{
                    p: 0,
                    color: CHECK_COLOR,
                    '& .MuiSvgIcon-root': { fontSize: 18 },
                    '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: ACTIVE_PURPLE },
                  }}
                />
              </TableCell>
            )}

            {showRowNumbers && (
              <TableCell sx={{ width: 44, borderBottom: `1px solid ${TABLE_BORDER}`, backgroundColor: HEADER_BG }} />
            )}

            {columns.map((col) => (
              <TableCell
                key={col.id}
                align={col.align || 'left'}
                sx={{
                  width: col.width,
                  minWidth: col.minWidth,
                  px: 2,
                  py: 0,
                  borderBottom: `1px solid ${TABLE_BORDER}`,
                  backgroundColor: HEADER_BG,
                  color: '#33313B',
                  fontSize: '12px',
                  fontWeight: 500,
                  letterSpacing: 0,
                  whiteSpace: 'nowrap',
                }}
              >
                {col.sortable ? (
                  <TableSortLabel
                    active={sortBy === col.id}
                    direction={sortBy === col.id ? sortDir : 'asc'}
                    onClick={() => handleSort(col.id)}
                    sx={{
                      color: 'inherit',
                      '&.Mui-active': { color: '#33313B' },
                      '& .MuiTableSortLabel-icon': { fontSize: 14, color: '#33313B !important' },
                    }}
                  >
                    {col.label}
                  </TableSortLabel>
                ) : col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={colSpan} sx={{ textAlign: 'center', py: 7, borderBottom: 'none' }}>
                <CircularProgress size={26} sx={{ color: ACTIVE_PURPLE }} />
              </TableCell>
            </TableRow>
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={colSpan} sx={{ textAlign: 'center', py: 8, borderBottom: 'none' }}>
                {emptyIcon && <Box sx={{ mb: 1, opacity: 0.2, '& svg': { fontSize: 48 } }}>{emptyIcon}</Box>}
                <Typography sx={{ fontWeight: 600, color: BODY_TEXT, fontSize: '13px' }}>{emptyMessage}</Typography>
                {emptyHint && <Typography sx={{ color: MUTED_TEXT, fontSize: '12px', mt: 0.5 }}>{emptyHint}</Typography>}
              </TableCell>
            </TableRow>
          ) : displayRows.map((row, idx) => {
            const rowId = getRowId(row, idx)
            const isSelected = selected.has(rowId)

            return (
              <TableRow
                key={rowId}
                selected={isSelected}
                onClick={() => onRowClick?.(row)}
                sx={{
                  height: 40,
                  cursor: onRowClick ? 'pointer' : 'default',
                  backgroundColor: '#FFFFFF',
                  transition: 'background-color 0.12s ease',
                  '&:hover': { backgroundColor: '#FAF9FC' },
                  '&.Mui-selected': { backgroundColor: '#F7F3FF' },
                  '&.Mui-selected:hover': { backgroundColor: '#F4EEFF' },
                  '&:last-of-type td': { borderBottom: pagination ? `1px solid ${TABLE_BORDER}` : 'none' },
                  ...getRowSx?.(row, idx, isSelected),
                }}
              >
                {selectable && (
                  <TableCell padding="none" sx={{ width: 40, pl: '10px', py: 0, borderBottom: `1px solid ${ROW_BORDER}`, verticalAlign: 'middle' }} onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      size="small"
                      checked={isSelected}
                      onChange={() => handleSelect(rowId)}
                      sx={{
                        p: 0,
                        color: CHECK_COLOR,
                        '& .MuiSvgIcon-root': { fontSize: 18 },
                        '&.Mui-checked': { color: ACTIVE_PURPLE },
                      }}
                    />
                  </TableCell>
                )}

                {showRowNumbers && (
                  <TableCell sx={{ width: 44, px: 2, borderBottom: `1px solid ${ROW_BORDER}`, color: MUTED_TEXT, fontSize: '12px' }}>
                    {activePagination ? (activePagination.page - 1) * activePagination.pageSize + idx + 1 : idx + 1}
                  </TableCell>
                )}

                {columns.map((col) => (
                  <TableCell
                    key={col.id}
                    align={col.align || 'left'}
                    sx={{
                      px: 2,
                      py: 0,
                      height: 40,
                      borderBottom: `1px solid ${ROW_BORDER}`,
                      color: BODY_TEXT,
                      fontSize: '13px',
                      fontWeight: 400,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {col.render ? col.render(row[col.id], row, idx) : (row[col.id] ?? '-')}
                  </TableCell>
                ))}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
      </TableContainer>

      {activePagination && (
        <Box
          sx={{
            minHeight: 56,
            flexShrink: 0,
            px: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 3,
            backgroundColor: '#FFFFFF',
            borderTop: `1px solid ${TABLE_BORDER}`,
          }}
        >
          <Typography sx={{ fontSize: '12px', color: MUTED_TEXT }}>
            {activePagination.total > 0
              ? `Showing ${(activePagination.page - 1) * activePagination.pageSize + 1} to ${Math.min(activePagination.page * activePagination.pageSize, activePagination.total)} of ${activePagination.total} ${activePagination.itemLabel ?? 'results'}`
              : `Showing 0 ${activePagination.itemLabel ?? 'results'}`}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Button
                size="small"
                disabled={activePagination.page <= 1}
                onClick={() => activePagination.onPageChange(1)}
                sx={{ minWidth: 18, px: 0.5, color: MUTED_TEXT, fontSize: '12px', textTransform: 'none' }}
              >
                {'<<'}
              </Button>
              <Button
                size="small"
                disabled={activePagination.page <= 1}
                onClick={() => activePagination.onPageChange(activePagination.page - 1)}
                sx={{ minWidth: 18, px: 0.5, color: MUTED_TEXT, fontSize: '12px', textTransform: 'none' }}
              >
                {'<'}
              </Button>
              <Button
                size="small"
                disabled={activePagination.page <= 1}
                onClick={() => activePagination.onPageChange(activePagination.page - 1)}
                sx={{ minWidth: 0, px: 1, color: MUTED_TEXT, fontSize: '12px', textTransform: 'none' }}
              >
                Previous
              </Button>

              {getVisiblePages(activePagination.page, Math.max(1, activePagination.totalPages)).map((item, index) => (
                item === 'ellipsis' ? (
                  <Typography key={`ellipsis-${index}`} sx={{ px: 0.5, color: MUTED_TEXT, fontSize: '12px' }}>...</Typography>
                ) : (
                  <Button
                    key={item}
                    size="small"
                    onClick={() => activePagination.onPageChange(item)}
                    sx={{
                      minWidth: 24,
                      width: 24,
                      height: 24,
                      p: 0,
                      borderRadius: '4px',
                      border: item === activePagination.page ? `1px solid ${PAGINATION_ACTIVE_FG}` : '1px solid transparent',
                      color: item === activePagination.page ? PAGINATION_ACTIVE_FG : BODY_TEXT,
                      backgroundColor: item === activePagination.page ? PAGINATION_ACTIVE_BG : 'transparent',
                      fontSize: '12px',
                    }}
                  >
                    {item}
                  </Button>
                )
              ))}

              <Button
                size="small"
                disabled={activePagination.page >= Math.max(1, activePagination.totalPages)}
                onClick={() => activePagination.onPageChange(activePagination.page + 1)}
                sx={{ minWidth: 0, px: 1, color: BODY_TEXT, fontSize: '12px', textTransform: 'none' }}
              >
                Next
              </Button>
              <Button
                size="small"
                disabled={activePagination.page >= Math.max(1, activePagination.totalPages)}
                onClick={() => activePagination.onPageChange(activePagination.page + 1)}
                sx={{ minWidth: 18, px: 0.5, color: MUTED_TEXT, fontSize: '12px', textTransform: 'none' }}
              >
                {'>'}
              </Button>
              <Button
                size="small"
                disabled={activePagination.page >= Math.max(1, activePagination.totalPages)}
                onClick={() => activePagination.onPageChange(Math.max(1, activePagination.totalPages))}
                sx={{ minWidth: 18, px: 0.5, color: MUTED_TEXT, fontSize: '12px', textTransform: 'none' }}
              >
                {'>>'}
              </Button>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Typography sx={{ fontSize: '12px', color: MUTED_TEXT }}>Page</Typography>
              <Select
                size="small"
                value={activePagination.pageSize}
                onChange={(e) => activePagination.onPageSizeChange?.(Number(e.target.value))}
                disabled={!activePagination.onPageSizeChange}
                sx={{
                  height: 28,
                  minWidth: 54,
                  fontSize: '12px',
                  backgroundColor: '#FAFAFC',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E8E5EF' },
                  '& .MuiSelect-select': { py: 0.25, pl: 1, pr: '24px !important' },
                }}
              >
                {(activePagination.pageSizeOptions ?? [10, 20, 50]).map((size) => (
                  <MenuItem key={size} value={size} sx={{ fontSize: '12px' }}>{size}</MenuItem>
                ))}
              </Select>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  )
}
